import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';
import { Button } from './button';
import { cn } from '@/lib/utils';

// Minimal AlertDialog built on top of Dialog — same API as shadcn AlertDialog

const AlertDialogContext = React.createContext<{ close: () => void }>({ close: () => {} });

interface AlertDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: React.ReactNode;
}

export function AlertDialog({ open, onOpenChange, children }: AlertDialogProps) {
  const close = React.useCallback(() => onOpenChange?.(false), [onOpenChange]);
  return (
    <AlertDialogContext.Provider value={{ close }}>
      <Dialog open={open} onOpenChange={onOpenChange}>
        {children}
      </Dialog>
    </AlertDialogContext.Provider>
  );
}

export function AlertDialogContent({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <DialogContent
      className={cn('max-w-md', className)}
      // Prevent closing on overlay click for destructive confirmations
      onInteractOutside={(e) => e.preventDefault()}
      {...(props as React.ComponentPropsWithoutRef<typeof DialogContent>)}
    >
      {children}
    </DialogContent>
  );
}

export function AlertDialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <DialogHeader className={className} {...props} />;
}

export function AlertDialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <DialogFooter className={className} {...props} />;
}

export function AlertDialogTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <DialogTitle className={className} {...(props as React.ComponentPropsWithoutRef<typeof DialogTitle>)} />;
}

export function AlertDialogDescription({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <DialogDescription className={className} {...(props as React.ComponentPropsWithoutRef<typeof DialogDescription>)} />;
}

export function AlertDialogCancel({ className, onClick, children, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { close } = React.useContext(AlertDialogContext);
  return (
    <Button
      variant="secondary"
      className={cn('focus-visible:ring-muted-foreground/40', className)}
      onClick={(e) => { close(); onClick?.(e); }}
      {...props}
    >
      {children ?? 'Cancel'}
    </Button>
  );
}

export function AlertDialogAction({ className, onClick, children, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <Button
      className={className}
      onClick={onClick}
      {...props}
    >
      {children ?? 'Continue'}
    </Button>
  );
}
